import { useCallback, useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import { useTranslations } from 'use-intl';
import { Body, Button, Row, Stack } from '@leanin/ui';
import {
  Dialog,
  DialogFooter,
  DialogSection,
  Slider,
  ToggleGroup,
  ToggleGroupItem,
} from '@leanin/ui/client';

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
// Crop-surface height. Responsive so the whole dialog — crucially its footer
// with the Save button — fits inside the modal's `max-height: 85vh` on short
// viewports (issue #172): it tracks the viewport but never drops below a usable
// floor and is capped at the original 320px on tall screens. The dialog body is
// also `scrollable`, so even the taller variants (the post composer's aspect
// toggle) or an extreme viewport keep the pinned footer reachable.
const FRAME_HEIGHT = 'clamp(200px, 45vh, 320px)';
const DEFAULT_MAX_OUTPUT_PX = 2048;

export type CropAspectOption = {
  /** Translated label shown in the aspect toggle. */
  label: string;
  /** Width / height. */
  value: number;
};

/** Crop rectangle in source-image pixel coordinates. */
export type ImageCropRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ImageCropDialogProps = {
  /** Raw picked file. The dialog never mutates it; onConfirm gets a new File. */
  file: File;
  /**
   * Restore a previously saved crop (source-pixel rect) so adjusting an
   * existing photo reopens at the current framing instead of resetting.
   */
  initialCrop?: ImageCropRect;
  /** Width / height of the destination surface, e.g. 1 for avatars, 4 for banners. */
  aspect: number;
  /**
   * When the destination has no single fixed shape (feed posts), offer these
   * aspects as a toggle. `aspect` must be one of the option values.
   */
  aspectOptions?: CropAspectOption[];
  /** Mask shape. 'round' previews circular display (avatars); output stays rectangular. */
  shape?: 'rect' | 'round';
  /** Dialog title override, e.g. "Crop cover photo". */
  title?: string;
  /** Cap on the longest edge of the exported image. */
  maxOutputPx?: number;
  /** Receives the cropped render plus the crop rect in source pixels. */
  onConfirm: (cropped: File, rect: ImageCropRect) => void;
  onCancel: () => void;
};

async function loadImage(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Image decode failed'));
      img.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Reads the crop rectangle from the full-resolution source so quality is
 * bounded by `maxOutputPx`, not by the on-screen preview size. PNG sources
 * stay PNG (logos need alpha); everything else exports as JPEG.
 */
async function exportCrop(file: File, area: Area, maxOutputPx: number): Promise<File> {
  const img = await loadImage(file);
  const scale = Math.min(1, maxOutputPx / Math.max(area.width, area.height));
  const width = Math.max(1, Math.round(area.width * scale));
  const height = Math.max(1, Math.round(area.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, width, height);

  const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob returned null'))),
      type,
      0.92,
    );
  });
  const ext = type === 'image/png' ? '.png' : '.jpg';
  return new File([blob], file.name.replace(/\.[^.]+$/, '') + ext, { type });
}

export function ImageCropDialog({
  file,
  initialCrop,
  aspect: initialAspect,
  aspectOptions,
  shape = 'rect',
  title,
  maxOutputPx = DEFAULT_MAX_OUTPUT_PX,
  onConfirm,
  onCancel,
}: ImageCropDialogProps) {
  const t = useTranslations('Common');

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [aspect, setAspect] = useState(initialAspect);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);
  // Set synchronously in handleApply — `saving` render state has a window
  // before the pending render commits where an Escape would run onCancel
  // while the export is in flight, so the parent would receive onConfirm
  // for a crop the user just rejected.
  const savingRef = useRef(false);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    setCrop({ x: 0, y: 0 });
    setZoom(MIN_ZOOM);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleAspectChange = useCallback((value: string) => {
    if (value === '') return;
    setAspect(Number(value));
    setCrop({ x: 0, y: 0 });
    setZoom(MIN_ZOOM);
  }, []);

  const handleApply = useCallback(async () => {
    if (!croppedAreaPixels || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      onConfirm(await exportCrop(file, croppedAreaPixels, maxOutputPx), {
        x: Math.round(croppedAreaPixels.x),
        y: Math.round(croppedAreaPixels.y),
        width: Math.round(croppedAreaPixels.width),
        height: Math.round(croppedAreaPixels.height),
      });
    } catch {
      // Decode/encode failure (corrupt file, canvas unavailable): treat as
      // cancel rather than stranding the dialog in a dead state.
      onCancel();
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [croppedAreaPixels, file, maxOutputPx, onConfirm, onCancel]);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      canDismiss={() => !savingRef.current}
      title={title ?? t('cropDialog.title')}
      description={t('cropDialog.instructions')}
    >
      <DialogSection gap="md" scrollable>
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: FRAME_HEIGHT,
            flexShrink: 0,
            background: 'var(--muted)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
          }}
        >
          {imageUrl && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              aspect={aspect}
              cropShape={shape === 'round' ? 'round' : 'rect'}
              showGrid={shape !== 'round'}
              initialCroppedAreaPixels={initialCrop}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        {aspectOptions && aspectOptions.length > 1 && (
          <Row gap="md" align="center">
            <Body size="sm" color="mute" style={{ flexShrink: 0 }}>
              {t('cropDialog.aspect')}
            </Body>
            <ToggleGroup
              type="single"
              value={String(aspect)}
              onValueChange={handleAspectChange}
              aria-label={t('cropDialog.aspect')}
            >
              {aspectOptions.map((option) => (
                <ToggleGroupItem key={String(option.value)} value={String(option.value)}>
                  {option.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </Row>
        )}

        <Row gap="md" align="center">
          <Body size="sm" color="mute" style={{ flexShrink: 0 }}>
            {t('cropDialog.zoom')}
          </Body>
          <div style={{ flex: 1 }}>
            <Slider
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.02}
              value={[zoom]}
              onValueChange={(values) => {
                if (values[0] !== undefined) setZoom(values[0]);
              }}
              aria-label={t('cropDialog.zoom')}
            />
          </div>
        </Row>
      </DialogSection>

      <DialogFooter>
        <Button variant="secondary" type="button" onClick={onCancel} disabled={saving}>
          {t('cropDialog.cancel')}
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={() => void handleApply()}
          disabled={saving || !croppedAreaPixels}
        >
          {t('cropDialog.apply')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
